import { useQuery } from "@apollo/client";
import {
  Badge,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  Table,
  Tabs,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { LOAN_ACCOUNTS, LOAN_PAYMENTS, LOAN_SCHEDULE } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

const LoanAccounts = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  // Payment tab state
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentSize, setPaymentSize] = useState(10);

  // Schedule tab state
  const [schedulePage, setSchedulePage] = useState(1);
  const [scheduleSize, setScheduleSize] = useState(10);

  const { data, loading, refetch } = useQuery(LOAN_ACCOUNTS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      status: statusFilter,
      orderBy: [
        {
          column: "created_at",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Accounts",
      });
    },
  });

  // Query for loan payments
  const {
    data: paymentsData,
    loading: paymentsLoading,
    refetch: refetchPayments,
  } = useQuery(LOAN_PAYMENTS, {
    variables: {
      loan_account_id: viewData?.id,
      first: paymentSize,
      page: paymentPage,
      orderBy: [
        {
          column: "created_at",
          order: "DESC",
        },
      ],
    },
    skip: !viewData?.id || activeTab !== "payments",
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Payments",
      });
    },
  });

  // Query for loan schedule
  const {
    data: scheduleData,
    loading: scheduleLoading,
    refetch: refetchSchedule,
  } = useQuery(LOAN_SCHEDULE, {
    variables: {
      loan_account_id: viewData?.id,
      first: scheduleSize,
      page: schedulePage,
      orderBy: [
        {
          column: "installment_no",
          order: "ASC",
        },
      ],
    },
    skip: !viewData?.id || activeTab !== "schedule",
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Schedule",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanAccounts.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const theme = useMantineTheme();

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "CLOSED":
        return "gray";
      case "DEFAULTED":
        return "red";
      case "PENDING":
        return "yellow";
      case "PARTIALLY_PAID":
        return "orange";
      case "WAIVED":
        return "blue";
      default:
        return "gray";
    }
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "TRX",
      key: "trx",
      width: "12%",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span style={{ fontWeight: 500 }}>{rowData.trx}</span>;
      },
    },
    {
      label: "Product",
      key: "product_name",
      sortable: false,
      searchable: true,
      width: "15%",
      render: (rowData) => {
        return <span>{rowData.product_name || "N/A"}</span>;
      },
    },
    {
      label: "Principal",
      key: "principal",
      sortable: true,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return (
          <span>
            {rowData.principal
              ? `ETB ${parseFloat(rowData.principal).toLocaleString()}`
              : "N/A"}
          </span>
        );
      },
    },
    {
      label: "Total Payable",
      key: "total_payable",
      sortable: true,
      searchable: false,
      width: "13%",
      render: (rowData) => {
        return (
          <span>
            {rowData.total_payable
              ? `ETB ${parseFloat(rowData.total_payable).toLocaleString()}`
              : "N/A"}
          </span>
        );
      },
    },
    {
      label: "Outstanding",
      key: "outstanding",
      sortable: true,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return (
          <span style={{ color: "#FF6A00", fontWeight: 500 }}>
            {rowData.outstanding
              ? `ETB ${parseFloat(rowData.outstanding).toLocaleString()}`
              : "N/A"}
          </span>
        );
      },
    },
    {
      label: "Interest Rate",
      key: "interest_rate",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return <span>{rowData.interest_rate || "N/A"}%</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: true,
      searchable: false,
      width: "11%",
      render: (rowData) => {
        return (
          <Badge color={getStatusColor(rowData.status)} variant="filled">
            {rowData.status || "N/A"}
          </Badge>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              onClick={() => handleViewAccount(rowData)}
            >
              <VisibilityIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: null,
  };

  const handleViewAccount = (account) => {
    setOpened(true);
    setViewData(account);
    setActiveTab("details");
    setPaymentPage(1);
    setSchedulePage(1);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={opened}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpened(false)}
        position="right"
        size="70%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        {viewData && (
          <div style={{ padding: "20px" }}>
            <h2 style={{ marginBottom: "20px" }}>
              Loan Account: {viewData.trx}
            </h2>
            <Tabs value={activeTab} onTabChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Tab value="details">Details</Tabs.Tab>
                <Tabs.Tab value="schedule">Schedule</Tabs.Tab>
                <Tabs.Tab value="payments">Payments</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="details" pt="md">
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <strong>TRX:</strong> {viewData.trx}
                  </div>
                  <div>
                    <strong>Product:</strong> {viewData.product_name}
                  </div>
                  <div>
                    <strong>Principal:</strong> ETB{" "}
                    {parseFloat(viewData.principal).toLocaleString()}
                  </div>
                  <div>
                    <strong>Interest Type:</strong> {viewData.interest_type}
                  </div>
                  <div>
                    <strong>Interest Rate:</strong> {viewData.interest_rate}%
                  </div>
                  <div>
                    <strong>Repayment Frequency:</strong>{" "}
                    {viewData.repayment_frequency}
                  </div>
                  <div>
                    <strong>Term:</strong> {viewData.term}
                  </div>
                  <div>
                    <strong>Service Charge:</strong> ETB{" "}
                    {parseFloat(viewData.service_charge || 0).toLocaleString()}
                  </div>
                  <div>
                    <strong>Processing Fee:</strong> ETB{" "}
                    {parseFloat(viewData.processing_fee || 0).toLocaleString()}
                  </div>
                  <div>
                    <strong>Insurance Fee:</strong> ETB{" "}
                    {parseFloat(viewData.insurance_fee || 0).toLocaleString()}
                  </div>
                  <div>
                    <strong>Total Payable:</strong> ETB{" "}
                    {parseFloat(viewData.total_payable).toLocaleString()}
                  </div>
                  <div>
                    <strong>Total Paid:</strong> ETB{" "}
                    {parseFloat(viewData.total_paid || 0).toLocaleString()}
                  </div>
                  <div>
                    <strong>Outstanding:</strong>{" "}
                    <span style={{ color: "#FF6A00", fontWeight: 600 }}>
                      ETB {parseFloat(viewData.outstanding).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <strong>Collateral Type:</strong>{" "}
                    {viewData.collateral_type || "N/A"}
                  </div>
                  <div>
                    <strong>Status:</strong>{" "}
                    <Badge color={getStatusColor(viewData.status)}>
                      {viewData.status}
                    </Badge>
                  </div>
                  {viewData.disbursed_at && (
                    <div>
                      <strong>Disbursed At:</strong>{" "}
                      {new Date(viewData.disbursed_at).toLocaleDateString()}
                    </div>
                  )}
                  {viewData.due_date && (
                    <div>
                      <strong>Due Date:</strong>{" "}
                      {new Date(viewData.due_date).toLocaleDateString()}
                    </div>
                  )}
                  <div>
                    <strong>Created At:</strong>{" "}
                    {new Date(viewData.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="schedule" pt="md">
                {scheduleLoading ? (
                  <LoadingOverlay
                    visible={true}
                    overlayBlur={2}
                    loader={customLoader}
                  />
                ) : (
                  <ScrollArea>
                    <Table striped highlightOnHover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Due Date</th>
                          <th>Principal Due</th>
                          <th>Interest Due</th>
                          <th>Total Due</th>
                          <th>Penalty</th>
                          <th>Amount Paid</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scheduleData?.loanSchedule?.data?.map((item) => (
                          <tr key={item.id}>
                            <td>{item.installment_no}</td>
                            <td>
                              {new Date(item.due_date).toLocaleDateString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                item.principal_due || 0,
                              ).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                item.interest_due || 0,
                              ).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(item.total_due || 0).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                item.penalty_due || 0,
                              ).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                item.amount_paid || 0,
                              ).toLocaleString()}
                            </td>
                            <td>
                              <Badge
                                color={
                                  item.status === "PAID"
                                    ? "green"
                                    : item.status === "OVERDUE"
                                      ? "red"
                                      : item.status === "PARTIALLY_PAID"
                                        ? "orange"
                                        : "yellow"
                                }
                              >
                                {item.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {scheduleData?.loanSchedule?.data?.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "gray",
                        }}
                      >
                        No schedule data available
                      </div>
                    )}
                  </ScrollArea>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="payments" pt="md">
                {paymentsLoading ? (
                  <LoadingOverlay
                    visible={true}
                    overlayBlur={2}
                    loader={customLoader}
                  />
                ) : (
                  <ScrollArea>
                    <Table striped highlightOnHover>
                      <thead>
                        <tr>
                          <th>TRX</th>
                          <th>Amount</th>
                          <th>Principal Paid</th>
                          <th>Interest Paid</th>
                          <th>Penalty Paid</th>
                          <th>Payment Method</th>
                          <th>Remark</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentsData?.loanPayments?.data?.map((payment) => (
                          <tr key={payment.id}>
                            <td>{payment.trx}</td>
                            <td>
                              ETB{" "}
                              {parseFloat(payment.amount || 0).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                payment.principal_paid || 0,
                              ).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                payment.interest_paid || 0,
                              ).toLocaleString()}
                            </td>
                            <td>
                              ETB{" "}
                              {parseFloat(
                                payment.penalty_paid || 0,
                              ).toLocaleString()}
                            </td>
                            <td>{payment.payment_method || "N/A"}</td>
                            <td>{payment.remark || "N/A"}</td>
                            <td>
                              {new Date(
                                payment.created_at,
                              ).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {paymentsData?.loanPayments?.data?.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "gray",
                        }}
                      >
                        No payment data available
                      </div>
                    )}
                  </ScrollArea>
                )}
              </Tabs.Panel>
            </Tabs>
          </div>
        )}
      </Drawer>

      {/* Filters Section */}
      <Card shadow="sm" p="md" mb="md">
        <Group spacing="md" align="flex-end">
          <Select
            label="Account Status"
            placeholder="All Statuses"
            clearable
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setActivePage(1);
            }}
            data={[
              { value: "ACTIVE", label: "Active" },
              { value: "CLOSED", label: "Closed" },
              { value: "DEFAULTED", label: "Defaulted" },
              { value: "PENDING", label: "Pending" },
              { value: "PARTIALLY_PAID", label: "Partially Paid" },
              { value: "WAIVED", label: "Waived" },
            ]}
            style={{ minWidth: 200 }}
            withinPortal
          />
        </Group>
      </Card>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanAccounts.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            //  optionsData={optionsData}
            loading={loading}
            data={data ? data.loanAccounts.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanAccounts;
